import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { IconFish } from './Icons';

export default function Navbar() {
  const { user, logout, isAdmin, isOrganizer } = useAuth();
  const { siteName, logoUrl } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Close drawer and profile dropdown on route change
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  // Escape key closes drawer/dropdown & body scroll lock
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setProfileOpen(false);
      }
    };
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeyDown);
    } else {
      document.body.style.overflow = '';
    }
    if (profileOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, profileOpen]);

  // Close profile dropdown on click outside
  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [profileOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const getInitials = () => {
    if (!user) return '';
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!openDropdown) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openDropdown]);

  // Close dropdown on route change
  useEffect(() => { setOpenDropdown(null); }, [location.pathname]);

  // Admin dropdown groups
  const adminDropdowns = isAdmin ? [
    {
      label: 'Events',
      items: [
        { to: '/admin/events', label: 'All Events' },
        { to: '/admin/organizers', label: 'Organizers' },
        { to: '/admin/participants', label: 'Participants' },
        { to: '/admin/teams', label: 'Teams' },
        { to: '/admin/sponsors', label: 'Event Sponsors' },
        { to: '/admin/advert-boards', label: 'Advert Boards' },
        { to: '/admin/analytics', label: 'Platform Analytics' },
      ]
    },
    {
      label: 'Settings',
      items: [
        { to: '/admin/theme', label: 'Theme' },
        { to: '/admin/pricing', label: 'Pricing Tiers' },
        { to: '/admin/bodies-of-water', label: 'Bodies of Water' },
        { to: '/admin/email-templates', label: 'Email Templates' },
        { to: '/admin/invoices', label: 'Invoices' },
        { to: '/admin/audit-log', label: 'Audit Log' },
      ]
    }
  ] : [];

  const navLinks = [];
  if (user) {
    if (isAdmin) {
      navLinks.push({ to: '/dashboard', label: 'Dashboard' });
      // Flat links for mobile drawer
      navLinks.push({ to: '/admin/theme', label: 'Theme', group: 'Settings' });
      navLinks.push({ to: '/admin/pricing', label: 'Pricing Tiers', group: 'Settings' });
      navLinks.push({ to: '/admin/bodies-of-water', label: 'Bodies of Water', group: 'Settings' });
      navLinks.push({ to: '/admin/email-templates', label: 'Email Templates', group: 'Settings' });
      navLinks.push({ to: '/admin/invoices', label: 'Invoices', group: 'Settings' });
      navLinks.push({ to: '/admin/audit-log', label: 'Audit Log', group: 'Settings' });
      navLinks.push({ to: '/admin/events', label: 'All Events', group: 'Events' });
      navLinks.push({ to: '/admin/organizers', label: 'Organizers', group: 'Events' });
      navLinks.push({ to: '/admin/participants', label: 'Participants', group: 'Events' });
      navLinks.push({ to: '/admin/teams', label: 'Teams', group: 'Events' });
      navLinks.push({ to: '/admin/sponsors', label: 'Event Sponsors', group: 'Events' });
      navLinks.push({ to: '/admin/advert-boards', label: 'Advert Boards', group: 'Events' });
      navLinks.push({ to: '/admin/analytics', label: 'Platform Analytics', group: 'Events' });
    } else if (isOrganizer) {
      navLinks.push({ to: '/dashboard', label: 'My Events' });
      navLinks.push({ to: '/events', label: 'All Events' });
      navLinks.push({ to: '/events/create', label: '+ New Event' });
      navLinks.push({ to: '/teams', label: 'Teams' });
    } else {
      navLinks.push({ to: '/dashboard', label: 'Dashboard' });
      navLinks.push({ to: '/events', label: 'Events' });
      navLinks.push({ to: '/teams', label: 'Teams' });
    }
  }

  const isDropdownActive = (items) => items.some(item => location.pathname === item.to);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-inner">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            {logoUrl ? (
              <img src={logoUrl} alt={siteName || 'DerbyScores'} style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
            ) : (
              <IconFish size="24px" />
            )}
          </Link>

          {/* Desktop nav links */}
          <div className="navbar-links" ref={dropdownRef}>
            {user ? (
              <>
                {isAdmin ? (
                  <>
                    <Link
                      to="/dashboard"
                      className={`navbar-link${isActive('/dashboard') ? ' active' : ''}`}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/circuit"
                      className={`navbar-link${isActive('/circuit') ? ' active' : ''}`}
                    >
                      Standings
                    </Link>
                    {adminDropdowns.map(({ label, items }) => (
                      <div key={label} className="navbar-dropdown-wrap">
                        <button
                          className={`navbar-link navbar-dropdown-trigger${isDropdownActive(items) ? ' active' : ''}`}
                          onClick={() => setOpenDropdown(openDropdown === label ? null : label)}
                        >
                          {label}
                          <svg width="10" height="6" viewBox="0 0 10 6" style={{ marginLeft: 4, opacity: 0.6 }}>
                            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                          </svg>
                        </button>
                        {openDropdown === label && (
                          <div className="navbar-dropdown-menu">
                            {items.map(item => (
                              <Link
                                key={item.to}
                                to={item.to}
                                className={`navbar-dropdown-item${isActive(item.to) ? ' active' : ''}`}
                                onClick={() => setOpenDropdown(null)}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  navLinks.map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className={`navbar-link${isActive(to) ? ' active' : ''}`}
                    >
                      {label}
                    </Link>
                  ))
                )}
                <div className="navbar-user" ref={profileRef}>
                  <button
                    className="navbar-avatar"
                    onClick={() => setProfileOpen(!profileOpen)}
                    aria-label="Profile menu"
                    style={{ border: 'none', cursor: 'pointer' }}
                  >
                    {getInitials()}
                  </button>
                  {profileOpen && (
                    <div className="profile-dropdown">
                      <div className="profile-dropdown-header">
                        <div className="navbar-user-name">
                          {user.first_name} {user.last_name}
                        </div>
                        <span className="badge badge-active" style={{ marginTop: 4 }}>
                          {user.role?.replace('_', ' ')}
                        </span>
                      </div>
                      <Link
                        to="/profile"
                        className="profile-dropdown-item"
                        onClick={() => setProfileOpen(false)}
                      >
                        Profile
                      </Link>
                      <div className="profile-dropdown-divider" />
                      <button
                        className="profile-dropdown-item"
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
              </>
            )}
          </div>

          {/* Hamburger button (mobile) */}
          {user && (
            <button
              className="navbar-hamburger"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`navbar-hamburger-icon${menuOpen ? ' open' : ''}`} />
            </button>
          )}

          {/* Mobile: login/signup when not logged in */}
          {!user && (
            <div className="navbar-mobile-auth">
              <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile drawer overlay */}
      <div
        className={`navbar-overlay${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div className={`navbar-drawer${menuOpen ? ' open' : ''}`}>
        <div className="navbar-drawer-links">
          {isAdmin ? (
            <>
              <Link
                to="/dashboard"
                className={`navbar-drawer-link${isActive('/dashboard') ? ' active' : ''}`}
              >
                Dashboard
              </Link>
              <Link
                to="/circuit"
                className={`navbar-drawer-link${isActive('/circuit') ? ' active' : ''}`}
              >
                Standings
              </Link>
              {adminDropdowns.map(({ label, items }) => (
                <React.Fragment key={label}>
                  <div className="navbar-drawer-group">{label}</div>
                  {items.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`navbar-drawer-link navbar-drawer-link-indent${isActive(item.to) ? ' active' : ''}`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </React.Fragment>
              ))}
            </>
          ) : (
            navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`navbar-drawer-link${isActive(to) ? ' active' : ''}`}
              >
                {label}
              </Link>
            ))
          )}
        </div>
        {user && (
          <div className="navbar-drawer-footer">
            <div className="navbar-drawer-user">
              <div className="navbar-avatar">{getInitials()}</div>
              <div className="navbar-user-info">
                <div className="navbar-user-name">
                  {user.first_name} {user.last_name}
                </div>
                <div className="navbar-user-role">{user.role}</div>
              </div>
            </div>
            <Link to="/profile" className="btn btn-ghost" style={{ width: '100%', marginBottom: 8 }}>
              Profile
            </Link>
            <button className="btn btn-ghost" onClick={handleLogout} style={{ width: '100%' }}>
              Logout
            </button>
          </div>
        )}
      </div>
    </>
  );
}
